import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import { typeSettingConfig } from "./typeSettingConfig";
import { DETAULT_ROLE, EMPLOYEE_ROLE, STAUTES } from "../../../../utils/constant";
import { getApi, postApi, putApi } from "../../../../utils/services";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { R_SETTINGS } from "../../../../utils/permissionReferenceConfig";
import usePermission from "../../../../utils/customHooks/usePermission";
import { useTheme } from "../../../ContextStore/ThemeContext";
import { useDispatch, useSelector } from "react-redux";
import { handleLogout, userSelector } from "../../../store/features/auth/authSlice";
import logo from "../../../Assets/Icons/logo.png";
import logoWhite from "../../../Assets/Icons/logo-white.png";
import menuopen from "../../../Assets/Icons/menu-open.png";
import menuclose from "../../../Assets/Icons/menu-close.png";
import NotificationIcon from "../../../Assets/IconComponents/Notification";
import { headerMenus } from "../../../../utils/MenusConfig";
import HelpMenu from "../../../Components/Header/HelpMenu";
import { ProTextInput, SettingFormsLayout, Layout } from "@proscore-ai/proscore-ui-library";

export default function TypeSettingForm() {
  const { id, settingType } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [popOverOpen, setPopOverOpen] = useState(false);
  const ref = useRef(null);
  const userData = useSelector(userSelector);
  const dispatch = useDispatch();

  const handleTabClick = tab => {
    navigate(`${tab.to}`);
  };
  const handleMenuOpen = () => setMenuOpen(!menuOpen);

  const onLogout = () => {
    dispatch(handleLogout());
  };

  const manageAccount = () => {
    setPopOverOpen(false);
  };

  const handleHome = () => {
    navigate("/clients");
  };
  const isAddMode = useMemo(() => id === "add", [id]);
  const [isSaving, setIsSaving] = useState(false);
  const [typeData, setTypeData] = useState({});
  const [mode, setMode] = useState(isAddMode ? "edit" : "read");
  const [status, setStatus] = useState(STAUTES.IDLE);

  const { handleSubmit, control, setValue } = useForm({ defaultValues: {} });
  const {
    idKey,
    perKey,
    typeKey,
    typeLabel,
    getByIdApiPath,
    addApiPath,
    subModuleRef,
    addFormName,
    updateApiPath,
  } = useMemo(() => typeSettingConfig[settingType] || {}, [settingType]);
  const { [addFormName]: formPermission } = usePermission(subModPermissionSelector(R_SETTINGS, subModuleRef), addFormName);
  const { fieldPerObj } = formPermission;
  const backAction = () => navigate(`/settings/${settingType}`);

  const getDetailById = useCallback(
    async (id) => {  
      setStatus(STAUTES.LOADING);
      const apiPath = `${getByIdApiPath}/${id}`;
      try {
        const response = await getApi(apiPath);
        if (!response || Object.keys(response).length === 0) {
          errorToast("No data found for this document type.");
          return;
        }
        setTypeData(response);
        setValue(typeKey, response[typeKey] || "");
      } catch (error) {
        errorToast("Failed to fetch data.");
      } finally {
        setStatus(STAUTES.IDLE);
      }
    },
    [setValue, typeKey, getByIdApiPath]
  );
  
  const onSubmit = async data => {
    if (isSaving) return; 
    setIsSaving(true);
    const payload = { ...typeData, ...data };
    setStatus(STAUTES.LOADING);
    const apiMethod = isAddMode ? postApi : putApi;
    const apiPath = isAddMode ? addApiPath : updateApiPath;
    const { error } = await apiMethod(apiPath, payload);
    setIsSaving(false);
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    successToast(`${typeLabel} ${isAddMode ? "added" : "updated"} successfully.`);
    backAction();
  };

  const onEdit = () => setMode("edit");

  const onCancel = () => {
    if (isAddMode) backAction();
    else setMode("read");
  };

  useEffect(() => {
    if (!typeKey) navigate("/");
  }, [typeKey, navigate]);

  useEffect(() => {
    if (!isAddMode) getDetailById(id);
  }, [id, isAddMode, getDetailById]);

  if (!typeKey) return null;
  const hideEdit = typeKey === "roleTypeName" && (typeData[typeKey] === DETAULT_ROLE || typeData[typeKey] === EMPLOYEE_ROLE);
  return (
    <Layout status={status}
      theme={theme}
      user={{
        userName: userData?.userName,
        email: userData?.email,
        avatar: userData?.avatar
      }}
      onLogout={onLogout}
      onTabClick={handleTabClick}
      onLogoClick={handleHome}
      logo={logo}
      logoWhite={logoWhite}
      menuOpenIcon={menuopen}
      menuCloseIcon={menuclose}
      menuTabs={headerMenus}
      NotificationIcon={NotificationIcon}
      HelpComponent={HelpMenu}
    >
      <SettingFormsLayout
        backAction={backAction}
        title={`${isAddMode ? "Add" : "Edit"} ${typeLabel}`}
        backLabel={`Go Back to All ${typeLabel}`}
        beingEdited={mode === "edit"}
        onEdit={onEdit}
        onCancel={onCancel}
        onSave={handleSubmit(onSubmit)}
        hideEdit={hideEdit}
        isSaving={isSaving}
      >
        <Grid container spacing={2}>
          <ProTextInput
            name={typeKey}
            label={typeLabel}
            control={control}
            mode={mode}
            isAddMode={isAddMode}
            formValues={typeData}
            rules={{ required: `${typeLabel} is required!` }}
            handleReadValueClick={hideEdit ? null : onEdit}
            permission={fieldPerObj[perKey]}
          />
        </Grid>
      </SettingFormsLayout>
    </Layout>
  );
}
